import { makeStyles } from '@material-ui/core/styles'
import { Grid, Theme, MenuItem } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  SemiConfigFormValues,
  useProductFromQueryParam,
} from '../../ProductsConfiguration'
import LiquidSelect from '@src/components/LiquidSelect'
import LiquidFormLabel from '@src/components/LiquidFormLabel'
import { useFormikContext } from 'formik'
import useSearch from '../../useSearch'
import { SemiConfigProductTypes } from '../../enum'

const useStyles = makeStyles((theme: Theme) => ({
  perPageAndSorting: {
    paddingBottom: theme.spacing(4),
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
  },
}))

const PerPageAndSortBy = ({ totalCount }: { totalCount: number }) => {
  const classes = useStyles()
  const intl = useIntl()
  const formik = useFormikContext<SemiConfigFormValues>()
  const { searchQuery } = useSearch()
  const {
    SANGERCRISPR,
    ORF,
    PREDESIGNED_SIRNA,
    PREDESIGNED_CRISPR_GRNA,
    PREDESIGNED_SHRNA,
  } = SemiConfigProductTypes

  const perPageOptions = [
    '10',
    '20',
    '30',
    '50',
    '100',
    intl.formatMessage({ id: 'ALL', defaultMessage: 'All' }),
  ]

  const perpageValue =
    totalCount === parseInt(formik.values.searchData.count)
      ? intl.formatMessage({ id: 'ALL', defaultMessage: 'All' })
      : formik.values.searchData.count

  const sortByValue = `${formik.values.searchData.sort}_${formik.values.searchData.order}`

  const sortByOptionsForCrisperGrna = [
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GRNA_ID_ASC',
        defaultMessage: 'gRNA ID (Ascending)',
      }),
      value: 'grnaId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GRNA_ID_DESC',
        defaultMessage: 'gRNA ID (Descending)',
      }),
      value: 'grnaId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_SYMBOL_ASC',
        defaultMessage: 'Gene Symbol (Ascending)',
      }),
      value: 'geneSymbol_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_SYMBOL_DESC',
        defaultMessage: 'Gene Symbol (Descending)',
      }),
      value: 'geneSymbol_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_ID_ASC',
        defaultMessage: 'Gene ID (Ascending)',
      }),
      value: 'sortGeneId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_ID_DESC',
        defaultMessage: 'Gene ID (Descending)',
      }),
      value: 'sortGeneId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GRNA_CATEGORY_ASC',
        defaultMessage: 'gRNA Category (Ascending)',
      }),
      value: 'gRNACategory_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GRNA_CATEGORY_DESC',
        defaultMessage: 'gRNA Category (Descending)',
      }),
      value: 'gRNACategory_DESC',
    },
  ]

  const sortByOptionsForSangerCrisper = [
    {
      label: intl.formatMessage({
        id: 'SORT_BY_SANGER_CLONE_ID_ASC',
        defaultMessage: 'Sanger Clone ID - ASC',
      }),
      value: 'sangerCloneId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_SANGER_CLONE_ID_DESC',
        defaultMessage: 'Sanger Clone ID - DESC',
      }),
      value: 'sangerCloneId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_ENSEMBL_GENE_SYMBOL_ASC',
        defaultMessage: 'Ensembl Gene Symbol - ASC',
      }),
      value: 'ensemblGeneSymbol_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_ENSEMBL_GENE_SYMBOL_DESC',
        defaultMessage: 'Ensembl Gene Symbol - DESC',
      }),
      value: 'ensemblGeneSymbol_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_ENSEMBL_GENE_ID_ASC',
        defaultMessage: 'Ensembl Gene ID ASC',
      }),
      value: 'ensemblGeneId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_ENSEMBL_GENE_ID_DESC',
        defaultMessage: 'Ensembl Gene ID DESC',
      }),
      value: 'ensemblGeneId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_NCBI_GENE_SYMBOL_ASC',
        defaultMessage: 'NCBI Gene Symbol - ASC',
      }),
      value: 'ncbiGeneSymbol_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_NCBI_GENE_SYMBOL_DESC',
        defaultMessage: 'NCBI Gene Symbol - DESC',
      }),
      value: 'ncbiGeneSymbol_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_NCBI_GENE_ID_ASC',
        defaultMessage: 'NCBI Gene ID ASC',
      }),
      value: 'ncbiGeneId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_NCBI_GENE_ID_DESC',
        defaultMessage: 'NCBI Gene ID DESC',
      }),
      value: 'ncbiGeneId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_SPECIES_ASC',
        defaultMessage: 'Species ASC',
      }),
      value: 'species_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_SPECIES_DESC',
        defaultMessage: 'Species DESC',
      }),
      value: 'species_DESC',
    },
  ]

  const sortByOptionsForORF = [
    {
      label: intl.formatMessage({
        id: 'SORT_BY_TRC_CLONE_ID_ASC',
        defaultMessage: 'TRC Clone ID - (Ascending)',
      }),
      value: 'cloneId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_TRC_CLONE_ID_DESC',
        defaultMessage: 'TRC Clone ID - (Descending)',
      }),
      value: 'cloneId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_SYMBOL_ASC',
        defaultMessage: 'Gene Symbol (Ascending)',
      }),
      value: 'geneSym_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_SYMBOL_DESC',
        defaultMessage: 'Gene Symbol (Descending)',
      }),
      value: 'geneSym_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_ORF_SEQUENCE_ASC',
        defaultMessage: 'ORF Seqeunce (Ascending)',
      }),
      value: 'orfSeq_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_ORF_SEQUENCE_DESC',
        defaultMessage: 'ORF Sequence (Descending)',
      }),
      value: 'orfSeq_DESC',
    },
  ]

  const sortByOptionsForShRNA = [
    {
      label: intl.formatMessage({
        id: 'SORT_BY_TRC_CLONE_ID_ASC_SHRNA',
        defaultMessage: 'TRC Clone ID (Ascending)',
      }),
      value: 'trcId_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_TRC_CLONE_ID_DESC_SHRNA',
        defaultMessage: 'TRC Clone ID (Descending)',
      }),
      value: 'trcId_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_SYMBOL_ASC_SHRNA',
        defaultMessage: 'Gene Symbol (Ascending)',
      }),
      value: 'geneSymbol_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_GENE_SYMBOL_DESC_SHRNA',
        defaultMessage: 'Gene Symbol (Descending)',
      }),
      value: 'geneSymbol_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_VALIDATED_ASC_SHRNA',
        defaultMessage: 'Validated (Ascending)',
      }),
      value: 'validated_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_VALIDATED_DESC_SHRNA',
        defaultMessage: 'Validated (Descending)',
      }),
      value: 'validated_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_REGION_ASC_SHRNA',
        defaultMessage: 'Region (Ascending)',
      }),
      value: 'region_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_REGION_DESC_SHRNA',
        defaultMessage: 'Region (Descending)',
      }),
      value: 'region_DESC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_TARGET_SEQUENCE_ASC_SHRNA',
        defaultMessage: 'Target Sequence (Ascending)',
      }),
      value: 'targetSeq_ASC',
    },
    {
      label: intl.formatMessage({
        id: 'SORT_BY_TARGET_SEQUENCE_DESC_SHRNA',
        defaultMessage: 'Target Sequence (Descending)',
      }),
      value: 'targetSeq_DESC',
    },
  ]

  const product = useProductFromQueryParam()

  const getSortByOptions = () => {
    switch (product) {
      case PREDESIGNED_CRISPR_GRNA:
        return sortByOptionsForCrisperGrna
      case SANGERCRISPR:
        return sortByOptionsForSangerCrisper
      case ORF:
        return sortByOptionsForORF
      case PREDESIGNED_SHRNA:
        return sortByOptionsForShRNA
      case PREDESIGNED_SIRNA:
        return sortByOptionsForCrisperGrna
      default:
        return sortByOptionsForCrisperGrna
    }
  }

  const sortByOptions = getSortByOptions()

  const handlePerPageChange = async (e) => {
    const countValue = e.target.value === 'All' ? totalCount : e.target.value
    formik.setFieldValue('searchData.count', countValue, false)
    formik.setFieldValue('searchData.page', 1, false)
    //trigger search
    await searchQuery({ count: countValue })
  }
  const handleSortByChange = async (e) => {
    const value = e.target.value.split('_')
    const sort = value[0] || ''
    const order = value[1] || ''
    formik.setFieldValue('searchData.sort', sort, false)
    formik.setFieldValue('searchData.order', order, false)
    formik.setFieldValue('searchData.page', 1, false)
    //trigger search
    await searchQuery({ sort: sort, order: order })
  }

  return (
    <Grid item xs={4} container className={classes.perPageAndSorting}>
      <Grid item xs={3}>
        <LiquidFormLabel htmlFor="contact-selector">
          <FormattedMessage
            id="SEMI_CONF_PER_PAGE_crispgrna"
            defaultMessage="Per Page"
          />
        </LiquidFormLabel>
        <LiquidSelect
          fullWidth={false}
          value={perpageValue}
          onChange={handlePerPageChange}
          id="srp-sort-options"
        >
          {perPageOptions.map((value) => (
            <MenuItem key={value} value={value} id={`sort-option-${value}`}>
              {value}
            </MenuItem>
          ))}
        </LiquidSelect>
      </Grid>
      <Grid item xs={8}>
        <LiquidFormLabel htmlFor="contact-selector">
          <FormattedMessage
            id="SEMI_CONF_SORTBY_crispgrna"
            defaultMessage="Sort By"
          />
        </LiquidFormLabel>
        <LiquidSelect
          fullWidth
          value={sortByValue}
          onChange={handleSortByChange}
          id="srp-sort-options"
        >
          {sortByOptions.map((sort) => (
            <MenuItem
              key={sort.value}
              value={sort.value}
              id={`sort-option-${sort.value}`}
            >
              {sort.label}
            </MenuItem>
          ))}
        </LiquidSelect>
      </Grid>
    </Grid>
  )
}

export default PerPageAndSortBy
